"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import { AlertTriangle, CheckCircle, Copy, MessageSquare, Sparkles, Loader2 } from "lucide-react"
import { useAuth } from "./auth/auth-context"

type ToneAnalysis = {
  formal: number
  friendly: number
  aggressive: number
  romantic: number
  professional: number
  manipulative: number
  passiveAgressive: number
}

type SentimentScore = {
  positive: number
  negative: number
  neutral: number
}

type DetailedAnalysis = {
  clarity: number
  engagement: number
  appropriateness: number
  effectiveness: number
  comments: string
}

type AnalysisResult = {
  id: string
  message: string
  improvedMessage: string
  score: number
  mode: string
  redFlags: string[]
  suggestions: string[]
  date: string
  toneAnalysis: ToneAnalysis
  sentiment: SentimentScore
  detailedAnalysis: DetailedAnalysis
}

type AnalyzeScreenProps = {
  setActiveScreen: (screen: string) => void
  onSaveResult?: () => void
}

export function AnalyzeScreen({ setActiveScreen, onSaveResult }: AnalyzeScreenProps) {
  const { user, isAnonymous, updateUser } = useAuth()
  const [message, setMessage] = useState("")
  const [mode, setMode] = useState("casual")
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState("")
  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [activeTab, setActiveTab] = useState("original")
  // const { uer, xxx, saveMessage } = saveMessageAnalysis()

  const analyzeMessage = async () => {
    if (!message.trim()) return

    setIsAnalyzing(true)
    setCurrentStep("Initializing analysis...")
    setProgress(10)

    try {
      // Show initial processing steps for better UX
      await simulateInitialProcessing(setCurrentStep, setProgress, mode)

      // Get token if user is logged in
      const token = localStorage.getItem("token")

      const response = await fetch("/api/analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          mode,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Analysis failed")
      }

      const analysisResult = await response.json()

      setCurrentStep("Processing results...")
      setProgress(90)

      // Simulate final processing for better UX
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Set result
      setResult(analysisResult)

      // If user is logged in, decrement scans
      if (user && user.plan === "free" && !isAnonymous) {
        updateUser({ scansLeft: Math.max(0, user.scansLeft - 1) })
      }
    } catch (error: any) {
      console.error("Error analyzing message:", error)
      // Show error to user
      alert(`Analysis failed: ${error.message}`)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Add this helper function for better UX during API calls
  const simulateInitialProcessing = async (
    setCurrentStep: (step: string) => void,
    setProgress: (progress: number) => void,
    mode: string,
  ) => {
    const steps = [
      { step: "Loading language model...", progress: 20 },
      { step: "Analyzing message structure...", progress: 30 },
      { step: "Evaluating tone patterns...", progress: 50 },
      { step: `Optimizing for ${mode} communication...`, progress: 70 },
      { step: "Generating improvement suggestions...", progress: 80 },
    ]

    for (const { step, progress } of steps) {
      setCurrentStep(step)
      setProgress(progress)
      await new Promise((resolve) => setTimeout(resolve, 300 + Math.random() * 300))
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 8) return "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
    if (score >= 5) return "bg-amber-500/20 text-amber-400 border border-amber-500/30"
    return "bg-rose-500/20 text-rose-400 border border-rose-500/30"
  }

  const getScoreEmoji = (score: number) => {
    if (score >= 8) return "🔥"
    if (score >= 5) return "😐"
    return "🚩"
  }

  const getModeEmoji = (mode: string) => {
    switch (mode) {
      case "casual":
        return "👋"
      case "professional":
        return "👔"
      case "flirty":
        return "😏"
      case "sarcastic":
        return "😒"
      default:
        return "💬"
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    alert("Copied to clipboard!")
  }

  const handleReset = () => {
    setResult(null)
    setMessage("")
    setActiveTab("original")
  }

  const handleSaveToHistory = () => {
    // This is now handled automatically by the API
    setActiveScreen("history")
  }

  // Check if user has scans left
  const canAnalyze = user?.plan === "premium" || isAnonymous || (user?.scansLeft && user.scansLeft > 0)

  return (
    <div className="flex flex-col h-full p-5 pt-8">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <h1 className="text-2xl font-light">
            <span className="gradient-text font-medium">Analyze</span> Message
          </h1>

          {user?.plan === "premium" ? (
            <Badge className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 px-3 py-1 text-xs font-medium text-amber-400">
              Premium
            </Badge>
          ) : (
            <Badge className="glass px-3 py-1 text-xs font-medium text-zinc-300">
              {user?.scansLeft || 3} scans left
            </Badge>
          )}
        </div>

        {!result && (
          <>
            <div className="mb-3">
              <Tabs value={mode} onValueChange={setMode} className="w-full">
                <TabsList className="grid w-full grid-cols-4 glass">
                  <TabsTrigger value="casual" className="text-xs">
                    <span className="mr-1 hidden sm:inline">👋</span> Casual
                  </TabsTrigger>
                  <TabsTrigger value="professional" className="text-xs">
                    <span className="mr-1 hidden sm:inline">👔</span> Professional
                  </TabsTrigger>
                  <TabsTrigger value="flirty" className="text-xs">
                    <span className="mr-1 hidden sm:inline">😏</span> Flirty
                  </TabsTrigger>
                  <TabsTrigger value="sarcastic" className="text-xs">
                    <span className="mr-1 hidden sm:inline">😒</span> Sarcastic
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <Textarea
              placeholder="Type your message here..."
              className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 min-h-[150px]"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <div className="mt-3">
              <Button
                className="w-full gradient-bg text-white hover:opacity-90 rounded-xl h-12"
                onClick={analyzeMessage}
                disabled={!message.trim() || isAnalyzing || !canAnalyze}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Analyzing...
                  </>
                ) : !canAnalyze ? (
                  "No scans left - Upgrade to Premium"
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Analyze Message
                  </>
                )}
              </Button>

              {!canAnalyze && user && !isAnonymous && (
                <div className="mt-2 text-center">
                  <Button
                    variant="link"
                    className="text-rose-400 hover:text-rose-300 text-sm"
                    onClick={() => setActiveScreen("upgrade")}
                  >
                    Upgrade to Premium for unlimited scans
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>

      {isAnalyzing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 flex flex-col items-center justify-center text-center px-4"
        >
          <div className="w-full max-w-md">
            <div className="mb-6">
              <Progress value={progress} className="h-2 bg-white/10" />
            </div>
            <p className="text-zinc-400 mb-2">{currentStep}</p>
            <p className="text-sm text-zinc-500">This may take a few seconds...</p>
          </div>
        </motion.div>
      )}

      {result && !isAnalyzing && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 overflow-auto">
          <div className="glass-card rounded-xl overflow-hidden mb-4">
            <div className="flex items-left justify-between p-4 border-b border-white/10">

              <div className="flex items-center space-x-2">

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex items-center">
                      <span className="text-xl mr-2">{getModeEmoji(result.mode)}</span>
                      <div>
                        <div className="text-sm font-medium capitalize">{result.mode}</div>
                        <div className="text-xs text-zinc-400">Just now</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between space-x-2">
                    <Badge className={`${getScoreColor(result.score)} px-3 py-1 rounded-full`}>
                      <span>
                        {getScoreEmoji(result.score)} {result.score}/10
                      </span>
                    </Badge>
                    </div>
                  </div>

                  <TabsList className="glass">
                    <TabsTrigger value="original" className="text-xs">
                      Original
                    </TabsTrigger>
                    <TabsTrigger value="improved" className="text-xs">
                      Improved
                    </TabsTrigger>
                  </TabsList>


                  <TabsContent value="original" className="p-4 text-sm">
                    <div className="mb-3">{result.message}</div>

                    {result.redFlags.length > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center text-rose-400 text-xs mb-1">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          <span>Red Flags</span>
                        </div>
                        <ul className="text-xs text-zinc-400 pl-4">
                          {result.redFlags.map((flag, idx) => (
                            <li key={idx} className="list-disc">
                              {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {result.suggestions.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center text-amber-400 text-xs mb-1">
                          <Sparkles className="h-3 w-3 mr-1" />
                          <span>Suggestions</span>
                        </div>
                        <ul className="text-xs text-zinc-400 pl-4">
                          {result.suggestions.map((suggestion, idx) => (
                            <li key={idx} className="list-disc">
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Detailed Analysis */}
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <h4 className="text-xs font-medium mb-2">Tone Analysis</h4>
                          <div className="grid grid-cols-5 gap-1">
                            {Object.entries(result.toneAnalysis).map(([tone, value]) => (
                              <div key={tone} className="flex flex-col items-center">
                                <div className="h-20 w-full bg-white/5 rounded-md relative overflow-hidden">
                                  <div
                                    className="absolute bottom-0 w-full bg-rose-500/50"
                                    style={{ height: `${value}%` }}
                                  ></div>
                                </div>
                                <span className="text-[10px] mt-1 capitalize">{tone}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-span-2">
                          <h4 className="text-xs font-medium mb-2">Sentiment</h4>
                          <div className="grid grid-cols-3 gap-1">
                            {Object.entries(result.sentiment).map(([sentiment, value]) => (
                              <div key={sentiment} className="flex flex-col items-center">
                                <div className="h-12 w-full bg-white/5 rounded-md relative overflow-hidden">
                                  <div
                                    className={`absolute bottom-0 w-full ${
                                      sentiment === "positive"
                                        ? "bg-emerald-500/50"
                                        : sentiment === "negative"
                                          ? "bg-rose-500/50"
                                          : "bg-blue-500/50"
                                    }`}
                                    style={{ height: `${value}%` }}
                                  ></div>
                                </div>
                                <span className="text-[10px] mt-1 capitalize">{sentiment}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-span-2">
                          <h4 className="text-xs font-medium mb-2">Effectiveness</h4>
                          <div className="grid grid-cols-4 gap-1">
                            {Object.entries(result.detailedAnalysis)
                              .filter(([key]) => key !== "comments")
                              .map(([metric, value]) => (
                                <div key={metric} className="flex flex-col items-center">
                                  <div className="relative">
                                    <svg className="w-12 h-12">
                                      <circle
                                        className="text-white/5"
                                        strokeWidth="2"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="20"
                                        cx="24"
                                        cy="24"
                                      />
                                      <circle
                                        className="text-rose-500"
                                        strokeWidth="2"
                                        strokeDasharray={`${(Number(value) / 10) * 125.6} 125.6`}
                                        strokeLinecap="round"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="20"
                                        cx="24"
                                        cy="24"
                                      />
                                    </svg>
                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px]">
                                      {value}/10
                                    </div>
                                  </div>
                                  <span className="text-[10px] mt-1 capitalize">{metric}</span>
                                </div>
                              ))}
                          </div>
                        </div>

                        {result.detailedAnalysis.comments && (
                          <div className="col-span-2 mt-2">
                            <h4 className="text-xs font-medium mb-1">Comments</h4>
                            <p className="text-xs text-zinc-400">{result.detailedAnalysis.comments}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>


                  <TabsContent value="improved" className="p-4 text-sm">
                    <div className="flex items-center text-emerald-400 text-xs mb-1">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      <span>Improved Version</span>
                    </div>
                    <div className="mb-4">{result.improvedMessage}</div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="glass text-xs"
                      onClick={() => handleCopy(result.improvedMessage)}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy to clipboard
                    </Button>
                  </TabsContent>

                </Tabs>
              </div>
            </div>


            
            <div className="flex border-t border-white/10">
              <Button
                variant="ghost"
                className="flex-1 rounded-none h-10 text-xs text-zinc-400 hover:text-white hover:bg-white/5"
                onClick={handleReset}
              >
                Analyze Another Message
              </Button>
              <Button
                variant="ghost"
                className="flex-1 rounded-none h-10 text-xs text-zinc-400 hover:text-white hover:bg-white/5"
                onClick={handleSaveToHistory}
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                View History
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

